import {
  createFrontendModule,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { Content } from '@backstage/core-components';
import CssBaseline from '@material-ui/core/CssBaseline';
import LightModeIcon from '@material-ui/icons/Brightness7';
import { rafayLightTheme } from './rafayTheme';
import { GeneralSettings } from './GeneralSettings';

// Named distinctly from Backstage's built-in 'light'/'dark' theme
// extensions (see @backstage/plugin-app's AppThemeApi.tsx) since the
// `themes` extension point is a multi-value input, not a single
// overridable slot — reusing the same name doesn't replace the built-ins,
// it just adds a second, ambiguous entry with the same id. The built-ins
// are explicitly disabled in app-config.yaml instead.
//
// Light is the only registered theme, so every user — new or existing,
// regardless of OS preference or a previously stored selection — always
// resolves to the new "cloud control plane" design (see AppThemeProvider's
// resolveTheme fallback chain: with only one theme installed, every branch
// of that chain lands on it regardless of shouldPreferDark).
const lightTheme = ThemeBlueprint.make({
  name: 'rafay-light',
  params: {
    theme: {
      id: 'rafay-light',
      title: 'Light',
      variant: 'light',
      icon: <LightModeIcon />,
      // CssBaseline lives here (not injected anywhere else in this app or
      // any @backstage/* package) specifically to reset the browser's
      // default `body { margin: 8px }` — without it, Page's own internal
      // `height: 100vh; overflow-y: auto` grid (see
      // @backstage/core-components' Page.esm.js) plus that leftover
      // margin pushes body's total height just past the viewport, so the
      // window itself grows a second, mostly-empty scrollbar alongside
      // Page's real one.
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={rafayLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

// Replaces the built-in "General" settings sub-page (disabled in
// app-config.yaml) so the Appearance card never shows the Light/Auto theme
// toggle — see GeneralSettings.tsx.
const generalSettingsPage = SubPageBlueprint.make({
  name: 'general',
  params: {
    path: 'general',
    title: 'General',
    loader: async () => (
      <Content>
        <GeneralSettings />
      </Content>
    ),
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [lightTheme],
});

export const settingsModule = createFrontendModule({
  pluginId: 'user-settings',
  extensions: [generalSettingsPage],
});
